import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useUser, useClerk } from "@clerk/clerk-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  CheckCircle2,
  ShieldAlert,
  BookOpen,
  ArrowRight,
  Sparkles,
  Users,
  LogOut,
  Layers,
  Briefcase,
  GraduationCap,
  Target,
  X,
  ChevronRight,
  Clock,
  UserPlus,
  RefreshCw,
} from "lucide-react";
import {
  getPublicOrgInfoApiV1B2bPublicSlugOrTokenGet,
  joinOrganizationApiV1B2bJoinSlugOrTokenPost,
  getOrgMembershipStatusApiV1B2bMembershipStatusSlugGet,
} from "@/src/api/sdk.gen";
import { WorkspaceCourseSelector, type JoinWorkspacePreview } from "@/src/components/WorkspaceCourseSelector";

interface CoursePreview {
  id: number;
  uuid: string;
  title: string;
  description?: string | null;
  cover_image_url?: string | null;
  total_modules: number;
  total_lessons: number;
}

interface OrgPublicInfo {
  id: number;
  name: string;
  slug: string;
  status: string;
  logo_url?: string | null;
  description?: string | null;
  industry?: string | null;
  allowed_domains: string[];
  departments: string[];
  join_link_enabled: boolean;
  requires_domain_match: boolean;
  sponsored_courses: CoursePreview[];
  workspaces: JoinWorkspacePreview[];
}

export default function B2BJoinPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const joinIdentifier = searchParams.get("token") || slug || "";
  const navigate = useNavigate();
  const { user, isLoaded: isUserLoaded, isSignedIn } = useUser();
  const { signOut } = useClerk();

  const [org, setOrg] = useState<OrgPublicInfo | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isDomainMismatch, setIsDomainMismatch] = useState(false);
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState<"none" | "pending" | "approved" | "rejected">("none");
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Onboarding profiling modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileStep, setProfileStep] = useState<1 | 2 | 3>(1);
  const [profileData, setProfileData] = useState({
    department: "",
    job_title: "",
    experience_level: "intermediate",
    learning_goals: [] as string[],
  });

  const autoJoinTriggered = useRef(false);

  useEffect(() => {
    async function loadOrgInfo() {
      if (!joinIdentifier) return;
      try {
        setLoading(true);
        const { data, error } = await getPublicOrgInfoApiV1B2bPublicSlugOrTokenGet({
          path: { slug_or_token: joinIdentifier },
        });
        if (error || !data) {
          setErrorMsg("Organization not found or invalid join link.");
          return;
        }
        const orgInfo = data as unknown as OrgPublicInfo;
        setOrg(orgInfo);
        setSelectedWorkspaceId(orgInfo.workspaces[0]?.id ?? null);
      } catch (err: any) {
        setErrorMsg("Failed to load organization information.");
      } finally {
        setLoading(false);
      }
    }
    loadOrgInfo();
  }, [joinIdentifier]);

  // Persist pending join URL in sessionStorage when unauthenticated
  useEffect(() => {
    if (!isSignedIn && slug && typeof window !== "undefined") {
      const returnUrl = `/join/${slug}${window.location.search}`;
      sessionStorage.setItem("inara_pending_join", returnUrl);
    }
  }, [isSignedIn, slug]);

  const checkStatus = async () => {
    if (!slug || !isSignedIn) return;
    try {
      setCheckingStatus(true);
      const { data } = await getOrgMembershipStatusApiV1B2bMembershipStatusSlugGet({
        path: { slug },
      });
      if (data && data.status) {
        setMembershipStatus(data.status as any);
      }
    } catch {
      // ignore
    } finally {
      setCheckingStatus(false);
    }
  };

  useEffect(() => {
    if (isSignedIn && isUserLoaded) {
      checkStatus();
    }
  }, [isSignedIn, isUserLoaded, slug]);

  // Auto-submit membership request if returning from a fresh sign-up
  const handleAutoSubmitJoin = async () => {
    try {
      setJoining(true);
      setErrorMsg(null);
      setIsDomainMismatch(false);

      const { data, error, response } = await joinOrganizationApiV1B2bJoinSlugOrTokenPost({
        path: { slug_or_token: joinIdentifier },
        body: {
          email: user?.primaryEmailAddress?.emailAddress,
          workspace_id: selectedWorkspaceId ?? undefined,
          department: profileData.department?.trim() || undefined,
          job_title: profileData.job_title?.trim() || undefined,
          experience_level: profileData.experience_level || "intermediate",
          learning_goals: profileData.learning_goals || [],
        },
      });

      if (error || !data) {
        const errObj = error as { error?: string; detail?: string };
        const msg = errObj?.error || errObj?.detail || "Failed to submit membership request.";
        if (response?.status === 403 && msg.includes("does not match")) {
          setIsDomainMismatch(true);
        }
        setErrorMsg(msg);
        return;
      }

      if (data.status === "approved" || (data.already_member && data.status !== "pending")) {
        setJoinSuccess(true);
        setMembershipStatus("approved");
        const dest = data.redirect_url || "/courses";
        setTimeout(() => navigate(dest), 1600);
      } else {
        setMembershipStatus("pending");
      }
    } catch (err: any) {
      setErrorMsg("An unexpected error occurred. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  useEffect(() => {
    if (!isSignedIn || !isUserLoaded || !slug || !org || autoJoinTriggered.current) return;
    const isAutoJoin =
      searchParams.get("auto_join") === "true" ||
      (typeof window !== "undefined" && sessionStorage.getItem("inara_pending_join")?.includes(slug));

    if (isAutoJoin && membershipStatus === "none") {
      autoJoinTriggered.current = true;
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("inara_pending_join");
      }
      handleAutoSubmitJoin();
    }
  }, [isSignedIn, isUserLoaded, slug, org, membershipStatus, searchParams]);


  const handleStartJoinFlow = () => {
    if (!isSignedIn) {
      const returnUrl = `/join/${slug}${window.location.search}`;
      navigate(`/sign-in?redirect_url=${encodeURIComponent(returnUrl)}`);
      return;
    }
    setShowProfileModal(true);
  };

  const handleFinalSubmitJoin = async () => {
    try {
      setJoining(true);
      setErrorMsg(null);
      setIsDomainMismatch(false);

      const { data, error, response } = await joinOrganizationApiV1B2bJoinSlugOrTokenPost({
        path: { slug_or_token: joinIdentifier },
        body: {
          email: user?.primaryEmailAddress?.emailAddress,
          workspace_id: selectedWorkspaceId ?? undefined,
          department: profileData.department?.trim() || undefined,
          job_title: profileData.job_title?.trim() || undefined,
          experience_level: profileData.experience_level,
          learning_goals: profileData.learning_goals,
        },
      });

      if (error || !data) {
        setShowProfileModal(false);
        const errObj = error as { error?: string; detail?: string };
        const msg = errObj?.error || errObj?.detail || "Failed to submit membership request.";
        if (response?.status === 403 && msg.includes("does not match")) {
          setIsDomainMismatch(true);
        }
        setErrorMsg(msg);
        return;
      }

      setShowProfileModal(false);
      if (data.status === "approved" || (data.already_member && data.status !== "pending")) {
        setJoinSuccess(true);
        setMembershipStatus("approved");
        const dest =
          data.redirect_url ||
          (data.role === "admin" ? `/org/${data.organization_slug || slug}/dashboard` : "/courses");
        setTimeout(() => {
          navigate(dest);
        }, 1600);
      } else {
        setMembershipStatus("pending");
      }
    } catch (err: any) {
      setErrorMsg("An unexpected error occurred. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  const handleSwitchAccount = async () => {
    await signOut(() => {
      const returnUrl = `/join/${slug}${window.location.search}`;
      navigate(`/sign-in?redirect_url=${encodeURIComponent(returnUrl)}`);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4">
        <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mb-4" />
        <p className="text-slate-400 font-medium">Loading organization workspace...</p>
      </div>
    );
  }

  if (!org || !org.join_link_enabled || org.status !== "approved") {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center shadow-2xl">
          <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {org?.status === "pending" ? "Organization Review Pending" : "Join Link Unavailable"}
          </h2>
          <p className="text-slate-400 text-sm mb-6 leading-relaxed">
            {org?.status === "pending"
              ? `The organization workspace for ${org.name} is currently awaiting platform approval. Once approved, employees will be able to join.`
              : (errorMsg || "This organization join link is currently disabled or expired. Please contact your company administrator.")}
          </p>
          <button
            onClick={() => navigate("/")}
            className="w-full py-3 px-4 bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold rounded-xl transition"
          >
            Return to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Background ambient lighting */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-indigo-600/10 blur-[120px] rounded-full pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-xl w-full"
      >
        {/* Main Card Container */}
        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 sm:p-10 shadow-2xl relative">
          
          {/* Header & Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex p-3 bg-gradient-to-tr from-indigo-600/20 to-purple-600/20 border border-indigo-500/30 rounded-2xl mb-4 shadow-inner">
              {org.logo_url ? (
                <img src={org.logo_url} alt={org.name} className="w-12 h-12 object-contain rounded-xl" />
              ) : (
                <Building2 className="w-8 h-8 text-indigo-400" />
              )}
            </div>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              B2B Enterprise Workspace
            </div>

            <h1 className="text-3xl font-extrabold text-white tracking-tight">
              Join {org.name}
            </h1>
            <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto leading-relaxed">
              {org.description || `Welcome to ${org.name}'s corporate learning academy on Inara.`}
            </p>
          </div>

          {/* Allowed Domains Restriction Notice */}
          {org.requires_domain_match && (
            <div className="mb-6 p-4 bg-slate-800/50 border border-slate-700/60 rounded-2xl flex items-start gap-3.5">
              <div className="p-2 bg-indigo-500/10 text-indigo-400 rounded-xl mt-0.5">
                <Users className="w-4 h-4" />
              </div>
              <div className="text-xs">
                <span className="font-semibold text-slate-200 block mb-1">
                  Corporate Email Domain Restricted
                </span>
                <span className="text-slate-400 leading-relaxed">
                  Only employees with verified email addresses matching{" "}
                  {org.allowed_domains.map((d) => (
                    <code key={d} className="px-1.5 py-0.5 bg-slate-900 border border-slate-700 text-indigo-300 rounded font-mono text-[11px] mx-0.5">
                      @{d}
                    </code>
                  ))}{" "}
                  can join this organization workspace.
                </span>
              </div>
            </div>
          )}

          <div className="mb-8">
            <WorkspaceCourseSelector
              workspaces={org.workspaces}
              selectedWorkspaceId={selectedWorkspaceId}
              onSelect={setSelectedWorkspaceId}
              appearance="dark"
            />
          </div>

          {/* Error Message */}
          {errorMsg && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-xs text-red-300 flex items-start gap-3">
              <ShieldAlert className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 leading-relaxed">
                {errorMsg}
                {isDomainMismatch && (
                  <button
                    onClick={handleSwitchAccount}
                    className="mt-3 flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 font-semibold underline underline-offset-2"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Sign in with corporate email
                  </button>
                )}
                {errorMsg.toLowerCase().includes("already associated") && (
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      onClick={() => navigate("/account")}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-white rounded-lg font-medium transition cursor-pointer"
                    >
                      Go to Account Settings
                    </button>
                    <button
                      onClick={handleSwitchAccount}
                      className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 font-semibold underline underline-offset-2 cursor-pointer"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      Switch Account
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Success Banner */}
          {joinSuccess && (
            <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl text-xs text-emerald-300 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <span>
                {membershipStatus === "approved"
                  ? `Successfully joined ${org.name}! Redirecting to your organization workspace...`
                  : `Membership request submitted for ${org.name}! Awaiting organization administrator approval.`}
              </span>
            </div>
          )}

          {/* Pending Approval Status Card */}
          {isSignedIn && membershipStatus === "pending" && (
            <div className="mb-6 p-5 bg-amber-500/10 border border-amber-500/20 rounded-2xl text-amber-200">
              <div className="flex items-start gap-3.5">
                <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl mt-0.5">
                  <Clock className="w-5 h-5 animate-pulse" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-sm text-amber-300">
                      Membership Request Pending Approval
                    </span>
                    <span className="px-2 py-0.5 bg-amber-500/20 border border-amber-500/30 text-amber-300 text-[10px] font-semibold uppercase rounded-full">
                      Awaiting Admin
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Your request to join <strong>{org.name}</strong> has been submitted to the Organization Admin for approval.
                    Once approved, you will automatically get access to the workspace and sponsored enterprise courses.
                  </p>
                  <div className="mt-3.5 flex flex-wrap items-center gap-3">
                    <button
                      onClick={checkStatus}
                      disabled={checkingStatus}
                      className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${checkingStatus ? "animate-spin text-amber-400" : ""}`} />
                      Check Approval Status
                    </button>
                    <button
                      onClick={() => navigate("/courses")}
                      className="text-xs text-slate-400 hover:text-slate-200 underline"
                    >
                      Browse Available Courses
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Approved Active Member Status Card */}
          {isSignedIn && membershipStatus === "approved" && (
            <div className="mb-6 p-5 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl text-emerald-200">
              <div className="flex items-start gap-3.5">
                <div className="p-2.5 bg-emerald-500/20 text-emerald-400 rounded-xl mt-0.5">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <span className="font-bold text-sm text-emerald-300 block mb-1">
                    Active Organization Member
                  </span>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    You are an approved member of <strong>{org.name}</strong>. You have full access to your team&apos;s workspace and enterprise courses.
                  </p>
                  <div className="mt-3.5">
                    <button
                      onClick={() => navigate("/courses")}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-md shadow-emerald-600/20 transition"
                    >
                      Go to Courses
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rejected Membership Status Card */}
          {isSignedIn && membershipStatus === "rejected" && (
            <div className="mb-6 p-5 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-200">
              <div className="flex items-start gap-3.5">
                <div className="p-2.5 bg-red-500/20 text-red-400 rounded-xl mt-0.5">
                  <ShieldAlert className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-sm text-red-300">
                      Membership Request Rejected
                    </span>
                    <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/30 text-red-300 text-[10px] font-semibold uppercase rounded-full">
                      Rejected
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Your request to join <strong>{org.name}</strong> was not approved by the Organization Administrator.
                  </p>
                  <div className="mt-3.5 flex items-center gap-3">
                    <button
                      onClick={handleStartJoinFlow}
                      disabled={joining}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-md shadow-indigo-600/20 transition disabled:opacity-50"
                    >
                      {joining ? "Resubmitting..." : "Resubmit Membership Request"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Current Auth User Status / Action Button */}
          {isUserLoaded && isSignedIn && (
            <div className="mb-6 p-3 bg-slate-800/30 border border-slate-800 rounded-xl flex items-center justify-between text-xs">
              <div className="text-slate-400">
                Signed in as: <strong className="text-slate-200">{user?.primaryEmailAddress?.emailAddress}</strong>
              </div>
              <button
                onClick={handleSwitchAccount}
                className="text-slate-400 hover:text-slate-200 font-medium underline"
              >
                Switch Account
              </button>
            </div>
          )}

          {/* Join / Request CTA Area */}
          {membershipStatus === "none" && (
            isSignedIn ? (
              <button
                onClick={handleStartJoinFlow}
                disabled={joining || joinSuccess}
                className="w-full py-4 px-6 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 disabled:opacity-50 text-white font-bold text-base rounded-2xl shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2.5 transition transform active:scale-[0.98]"
              >
                {joining ? "Submitting Request..." : "Request to Join Workspace"}
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => {
                    const returnUrl = `/join/${slug}${window.location.search}`;
                    navigate(`/sign-in?redirect_url=${encodeURIComponent(returnUrl)}`);
                  }}
                  className="w-full py-3.5 px-6 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-bold text-sm rounded-2xl shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 transition transform active:scale-[0.98]"
                >
                  Sign In to Join Workspace
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    const returnUrl = `/join/${slug}${window.location.search}`;
                    navigate(`/sign-up?redirect_url=${encodeURIComponent(returnUrl)}`);
                  }}
                  className="w-full py-3 px-6 bg-slate-800/80 hover:bg-slate-700 border border-slate-700 text-slate-200 font-semibold text-sm rounded-2xl flex items-center justify-center gap-2 transition"
                >
                  <UserPlus className="w-4 h-4 text-indigo-400" />
                  New to Inara? Create an Account
                </button>
              </div>
            )
          )}
        </div>
      </motion.div>

      {/* 3-Step Department & Role Profiling Modal */}
      <AnimatePresence>
        {showProfileModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative overflow-hidden"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-800">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-indigo-400 mb-1">
                    Step {profileStep} of 3 • Learner Profiling
                  </div>
                  <h3 className="text-lg font-bold text-white">Customize Your Workspace</h3>
                </div>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="text-slate-400 hover:text-white p-1 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Step 1: Department */}
              {profileStep === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    <Briefcase className="w-4 h-4 text-indigo-400" />
                    Which department or team do you belong to?
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    {(org.departments.length > 0 ? org.departments : ["Engineering", "Product", "Sales", "HR", "Operations", "Finance", "Other"]).map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setProfileData((prev) => ({ ...prev, department: d }))}
                        className={`py-3 px-4 rounded-xl text-xs font-semibold border text-left transition ${
                          profileData.department === d
                            ? "bg-indigo-600 border-indigo-500 text-white shadow-md shadow-indigo-600/20"
                            : "bg-slate-950 border-slate-800 text-slate-300 hover:bg-slate-800"
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Role / Job Title */}
              {profileStep === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    <Target className="w-4 h-4 text-indigo-400" />
                    What is your role or job title?
                  </div>
                  <input
                    type="text"
                    placeholder="e.g. Senior Software Engineer, Account Executive..."
                    value={profileData.job_title}
                    onChange={(e) => setProfileData((prev) => ({ ...prev, job_title: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-sm text-slate-100 focus:outline-none focus:border-indigo-500"
                    autoFocus
                  />
                  <div className="text-[11px] text-slate-400">
                    Popular in {profileData.department}: Software Engineer, Technical Lead, Product Manager, Analyst.
                  </div>
                </div>
              )}

              {/* Step 3: Experience Level */}
              {profileStep === 3 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    <GraduationCap className="w-4 h-4 text-indigo-400" />
                    What is your current AI / Technical experience level?
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { key: "beginner", label: "Beginner", desc: "New to AI and prompt engineering concepts" },
                      { key: "intermediate", label: "Intermediate", desc: "Familiar with AI tools and coding fundamentals" },
                      { key: "advanced", label: "Advanced", desc: "Experienced building AI models, pipelines, or systems" },
                    ].map((lvl) => (
                      <button
                        key={lvl.key}
                        type="button"
                        onClick={() => setProfileData((prev) => ({ ...prev, experience_level: lvl.key }))}
                        className={`w-full p-3.5 rounded-xl text-left border transition ${
                          profileData.experience_level === lvl.key
                            ? "bg-indigo-600/20 border-indigo-500 text-white"
                            : "bg-slate-950 border-slate-800 text-slate-300 hover:bg-slate-800/50"
                        }`}
                      >
                        <div className="font-bold text-xs text-white">{lvl.label}</div>
                        <div className="text-[11px] text-slate-400">{lvl.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Modal Action Controls */}
              <div className="mt-8 pt-4 border-t border-slate-800 flex items-center justify-between">
                {profileStep > 1 ? (
                  <button
                    type="button"
                    onClick={() => setProfileStep((s) => (s - 1) as any)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl"
                  >
                    Back
                  </button>
                ) : <div />}

                {profileStep < 3 ? (
                  <button
                    type="button"
                    onClick={() => setProfileStep((s) => (s + 1) as any)}
                    className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-md shadow-indigo-600/20"
                  >
                    Next Step
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleFinalSubmitJoin}
                    disabled={joining}
                    className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-indigo-600 hover:from-emerald-500 hover:to-indigo-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-600/30 flex items-center gap-2"
                  >
                    {joining ? "Submitting Request..." : "Complete & Submit Membership Request"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
